import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Container, ContentTile, Grid, GridSkeleton, Input, SectionHeader } from "@athena/ui";
import { Plus, RefreshCw, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client/react";
import { toast } from "sonner";
import { useAllRows } from "@/hooks/useAllRows";
import { CoursesDocument, DeleteCourseDocument, type CoursesQuery } from "../../graphql/courses.generated";
import { ResetAllCourseThumbnailsDocument } from "../../graphql/media.generated";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { usePlatformAdmin } from "@/lib/usePlatformAdmin";
import { DeleteDialog } from "@/components/DeleteDialog";
import { env } from "@/lib/env";

type FilterTab = "all" | "published" | "draft";
type Course = CoursesQuery["courses"][number];

export function CoursesPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isPlatformAdmin } = usePlatformAdmin();
    const [
        resetOpen,
        setResetOpen,
    ] = useState<boolean>(false);
    const [
        resetAllThumbnails,
        { loading: resettingThumbnails },
    ] = useMutation(ResetAllCourseThumbnailsDocument);

    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        filter,
        setFilter,
    ] = useState<FilterTab>("all");
    const [
        deleteTarget,
        setDeleteTarget,
    ] = useState<Course | null>(null);

    // Every course. The tab counts below are computed from this array, so a first-page-only read
    // did not just hide courses — it reported "50 published" as a fact.
    const {
        rows: courses,
        loading,
        refetch,
    } = useAllRows<Course>(CoursesDocument, (d) => (d as CoursesQuery | undefined)?.courses);
    const [
        deleteCourse,
        { loading: deleting },
    ] = useMutation(DeleteCourseDocument, {
        update(cache, { data }) {
            const deleted = data?.deleteCourse;
            if (!deleted) return;
            cache.evict({ id: cache.identify({ __typename: "Course", id: deleted.id }) });
            cache.gc();
        },
    });

    // Keyed on the rows, not `loading`: Apollo sets loading on every refetch, so gating
    // the whole page on it replaces live content — and unmounts any child holding
    // state or an in-flight request — every time something reloads.
    if (loading && courses.length === 0) return <GridSkeleton />;

    const publishedCount = courses.filter((c) => c.status === "PUBLISHED").length;
    const draftCount = courses.filter((c) => c.status === "DRAFT").length;

    const handleDeleteCourse = (courseId: string) => {
        const target = courses.find((c) => c.id === courseId);
        if (target) setDeleteTarget(target);
    };

    const handleCopyLink = async (courseId: string) => {
        const url = `${env.webAppUrl}/learn/${courseId}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Course link copied.");
        } catch {
            toast.error("Couldn't copy the course link.");
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        await deleteCourse({ variables: { id: deleteTarget.id } });
        setDeleteTarget(null);
    };

    const filtered = courses.filter((c) => {
        const matchesFilter =
            filter === "all" ||
            (filter === "published" && c.status === "PUBLISHED") ||
            (filter === "draft" && c.status === "DRAFT");
        const matchesSearch = c.title.toLowerCase().includes(search.toLowerCase());
        return matchesFilter && matchesSearch;
    });
    return (
        <Container className="p-6">
            <SectionHeader
                eyebrow={t("admin.lms.library")}
                title={t("nav.courses")}
                description={t("admin.lms.subTitle")}
                actions={
                    <div className="flex items-center gap-2">
                        {/* Platform admins only. It rewrites every course's cover, and an org
                            admin has no claim on the global library these all live in. */}
                        {isPlatformAdmin && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1.5"
                                onClick={() => setResetOpen(true)}
                            >
                                <RefreshCw className="h-3.5 w-3.5" />
                                Reset thumbnails
                            </Button>
                        )}
                        <Button onClick={() => navigate("/course/new")} size="sm" className="flex items-center gap-1.5">
                            <Plus className="h-3.5 w-3.5" />
                            {t("admin.lms.new")}
                        </Button>
                    </div>
                }
            />
            <div className="mb-6 flex items-center gap-3">
                <div className="relative max-w-sm flex-1">
                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                    <Input
                        placeholder={t("admin.lms.searchPlaceholder")}
                        className="pl-9"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        type="text"
                    />
                </div>
                <div className="bg-muted flex items-center gap-1 rounded-lg p-1">
                    {(
                        [
                            "all",
                            "published",
                            "draft",
                        ] as FilterTab[]
                    ).map((tab) => {
                        const label =
                            tab === "all"
                                ? `${t("admin.status.all")} ${courses.length}`
                                : tab === "published"
                                  ? `${t("admin.status.published")} ${publishedCount}`
                                  : `${t("admin.status.draft")} ${draftCount}`;
                        return (
                            <button
                                key={tab}
                                onClick={() => setFilter(tab)}
                                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                                    filter === tab
                                        ? "bg-background text-foreground font-medium shadow-sm"
                                        : "text-muted-foreground hover:text-foreground"
                                }`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>
            <Grid cols={3} gap="md" className="w-full items-stretch">
                {filtered.map((course) => (
                    <ContentTile
                        key={course.id}
                        title={course.title}
                        posterUrl={course.thumbnailUrl}
                        isPlaylist={true}
                        playlistLabel={`${course.content.length.toString()} videos`}
                        organizationName={course.shared ? "Global Library" : ""}
                        publishedStatus={course.status}
                        onClick={() => navigate(`/course-preview/${course.id}`)}
                        onCopyLinkClicked={() => void handleCopyLink(course.id)}
                        onDeleteClicked={() => handleDeleteCourse(course.id)}
                        // Its own, its lessons' and its videos' — what the course is about.
                        tags={course.allTags}
                    />
                ))}
            </Grid>
            <DeleteDialog
                open={!!deleteTarget}
                onOpenChange={(o) => {
                    if (!o) setDeleteTarget(null);
                }}
                title="Delete Course"
                deleteTarget={deleteTarget?.title}
                onConfirm={handleDelete}
                loading={deleting}
            />
            <ConfirmDialog
                open={resetOpen}
                onOpenChange={setResetOpen}
                title="Reset every course thumbnail"
                description={`Points all ${courses.length} course covers at the poster of the first video in each. A course whose first video has no poster yet keeps the cover it has. Covers somebody chose by hand are replaced.`}
                confirmLabel={`Reset ${courses.length} thumbnails`}
                loadingLabel="Resetting…"
                loading={resettingThumbnails}
                onConfirm={async () => {
                    try {
                        const res = await resetAllThumbnails();
                        toast.success(`${res.data?.resetAllCourseThumbnails ?? 0} thumbnails updated`);
                        void refetch();
                    } catch {
                        toast.error("Could not reset the thumbnails");
                    }
                    setResetOpen(false);
                }}
            />
        </Container>
    );
}
